/**
 * Home or business? — the customer's type (`customers.customer_type`).
 *
 * Corbin 2026-10-28: new customers start with NO type. The office picks Home
 * or Business; nothing defaults to Business any more, because a house left on
 * the old default paid the business city fee.
 *
 *   <HomeOrBusinessButtons>  — the two big buttons on the New customer page.
 *   <CustomerTypeHeaderPick> — the customer page header: a calm amber
 *                              "Home or business?" with one-tap buttons when
 *                              the type is blank, the plain word otherwise.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Home } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { InfoButton, type InfoTopic } from "@/components/info-button";
import { cn } from "@/lib/utils";

export type CustomerType = "residential" | "commercial";

export const HOME_OR_BUSINESS_HELP: InfoTopic = {
    title: "Home or business?",
    lines: [
        "Home = a house someone lives in.",
        "Business = everything else: stores, schools, HOAs, apartments.",
        "Some cities charge homes a different fee, so pick the right one.",
    ],
};

/** The plain word for a type, or null when nobody has picked. */
export function customerTypeWord(t: string | null | undefined): "Home" | "Business" | null {
    const s = (t ?? "").trim().toLowerCase();
    if (s === "residential") return "Home";
    if (s === "commercial") return "Business";
    return null;
}

/** Two big buttons: Home / Business, with the ⓘ. */
export function HomeOrBusinessButtons({
    value,
    onChange,
    error,
}: {
    value: CustomerType | null;
    onChange: (v: CustomerType) => void;
    /** Shown in red under the buttons (e.g. "Pick Home or Business."). */
    error?: string | null;
}) {
    const options: { v: CustomerType; label: string; icon: typeof Home }[] = [
        { v: "residential", label: "Home", icon: Home },
        { v: "commercial", label: "Business", icon: Building2 },
    ];
    return (
        <div className="space-y-1.5" data-testid="home-or-business">
            <div className="flex items-center gap-2">
                <span id="home-or-business-label" className="text-sm font-medium">
                    Home or business? <span className="text-destructive">*</span>
                </span>
                <InfoButton topic={HOME_OR_BUSINESS_HELP} />
            </div>
            <div role="radiogroup" aria-labelledby="home-or-business-label" className="grid grid-cols-2 gap-3">
                {options.map(({ v, label, icon: Icon }) => {
                    const on = value === v;
                    return (
                        <button
                            key={v}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            onClick={() => onChange(v)}
                            className={cn(
                                "flex min-h-14 items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-base font-semibold transition-colors",
                                on
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border bg-card text-foreground hover:bg-muted",
                                error && !value && "border-destructive/60",
                            )}
                        >
                            <Icon className="h-5 w-5" aria-hidden />
                            {label}
                        </button>
                    );
                })}
            </div>
            {error && !value && <p className="text-xs text-destructive">{error}</p>}
        </div>
    );
}

/**
 * The customer page header's type. Blank → a calm amber "Home or business?"
 * with Home / Business buttons that save in one tap. Set → the plain word.
 */
export function CustomerTypeHeaderPick({
    customerId,
    customerType,
}: {
    customerId: string;
    customerType: string | null | undefined;
}) {
    const qc = useQueryClient();
    const save = useMutation({
        mutationFn: (v: CustomerType) => api.patch(`/api/customers/${customerId}`, { customerType: v }),
        onSuccess: (_r, v) => {
            toast.success(`Saved: ${v === "residential" ? "Home" : "Business"}`);
            qc.invalidateQueries({ queryKey: ["customer", customerId] });
            qc.invalidateQueries({ queryKey: ["customers"] });
        },
        onError: () => toast.error("Couldn't save Home or business. Try again."),
    });

    const word = customerTypeWord(customerType);
    if (word) return <span data-testid="customer-type-word">{word}</span>;

    return (
        <span
            data-testid="customer-type-pick"
            className="inline-flex flex-wrap items-center gap-2 rounded-full border border-amber-400/70 bg-amber-50 px-2.5 py-1 text-sm text-amber-900 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-200"
        >
            <span className="font-medium">Home or business?</span>
            {(
                [
                    ["residential", "Home"],
                    ["commercial", "Business"],
                ] as const
            ).map(([v, label]) => (
                <button
                    key={v}
                    type="button"
                    disabled={save.isPending}
                    onClick={() => save.mutate(v)}
                    className="min-h-8 rounded-full border border-amber-500/60 bg-card px-3 text-sm font-semibold text-foreground hover:bg-amber-100 disabled:opacity-60 dark:hover:bg-amber-500/20"
                >
                    {label}
                </button>
            ))}
            <InfoButton topic={HOME_OR_BUSINESS_HELP} className="h-6 w-6 text-xs" />
        </span>
    );
}
